import { Timestamp, TranslateFunction } from '@griply/types';
import {
  differenceInCalendarDays,
  differenceInHours,
  format,
  isAfter,
  isSameYear,
} from 'date-fns';

export type GetTimeLeftLabelOptions = {
  today: Timestamp;
  t: TranslateFunction;
  short?: boolean;
  midShort?: boolean;
  isCompleted?: boolean;
};

export type TimeLeftLabel = {
  label: string;
  isOverdue: boolean;
  isLastWeek: boolean;
  isCompleted: boolean;
};

export const getTimeLeftLabel = (
  date: Timestamp,
  { today, t, short, midShort, isCompleted }: GetTimeLeftLabelOptions,
): TimeLeftLabel => {
  if (isCompleted) {
    const dateFormat = isSameYear(date, today) ? 'MMM d' : "MMM d, ''yy";
    return {
      label: format(date, dateFormat),
      isOverdue: false,
      isLastWeek: false,
      isCompleted: true,
    };
  }

  const isOverdue = isAfter(today, date);
  const hoursLeft = differenceInHours(date, today);
  const daysLeft = isOverdue
    ? differenceInCalendarDays(date, today)
    : Math.ceil(hoursLeft / 24);
  const yearsLeftCount = daysLeft / 365;
  const yearsLeft =
    yearsLeftCount % 1 === 0
      ? Math.floor(yearsLeftCount)
      : yearsLeftCount.toFixed(1);
  const monthsLeft = Math.floor(daysLeft / 30);
  const weeksLeft = Math.floor(daysLeft / 7);
  const isLastWeek = daysLeft <= 7;

  const overdueLabel = short
    ? 'general.labels.days-ago.short'
    : midShort
      ? 'general.labels.days-ago.mid-short'
      : 'general.labels.days-ago';
  const yearsLeftLabel = short
    ? 'general.labels.years-left.short.title'
    : midShort
      ? 'general.labels.years-left.mid-short.title'
      : 'general.labels.years-left.title';
  const monthsLeftLabel = short
    ? 'general.labels.months-left.short.title'
    : midShort
      ? 'general.labels.months-left.mid-short.title'
      : 'general.labels.months-left.title';
  const weeksLeftLabel = short
    ? 'general.labels.weeks-left.short.title'
    : midShort
      ? 'general.labels.weeks-left.mid-short.title'
      : 'general.labels.weeks-left.title';
  const daysLeftLabel = short
    ? 'general.labels.days-left.short.title'
    : midShort
      ? 'general.labels.days-left.mid-short.title'
      : 'general.labels.days-left.title';

  const key = isOverdue
    ? overdueLabel
    : daysLeft >= 365
      ? yearsLeftLabel
      : daysLeft >= 30
        ? monthsLeftLabel
        : daysLeft >= 15
          ? weeksLeftLabel
          : daysLeftLabel;

  const overdueDaysValue = short ? daysLeft : Math.abs(daysLeft);

  const amount = isOverdue
    ? overdueDaysValue
    : daysLeft >= 365
      ? yearsLeft
      : daysLeft >= 30
        ? monthsLeft
        : daysLeft >= 15
          ? weeksLeft
          : daysLeft;

  const count = isOverdue
    ? overdueDaysValue
    : daysLeft >= 365
      ? yearsLeftCount
      : daysLeft >= 30
        ? monthsLeft
        : daysLeft >= 15
          ? weeksLeft
          : daysLeft;

  return {
    label: t(key, { amount, count }),
    isOverdue,
    isLastWeek,
    isCompleted: false,
  };
};
